import React, { useState, useEffect } from 'react';
import { EndointDetailsTypes } from './basics';
import Urls from '../../../utils/util';

type codeSnippetType = 'CURL' | 'WSCAT' | 'NodeJs' | 'Python' | 'Go' | 'none';
type Props = {
  setIsCopiedTxt: React.Dispatch<React.SetStateAction<boolean>>;
  endpointData: EndointDetailsTypes;
};
function Snippet({ setIsCopiedTxt, endpointData }: Props) {
  const [codeSnippet, setCodeSnippet] = useState<codeSnippetType>('CURL');
  const [codeType, setCodeType] = useState<'https' | 'wss'>('https');
  const [codes, setCodes] = useState<
    { lang: codeSnippetType; http?: string; wss?: string }[]
  >([]);

  const copyText = (val: string) => {
    const divId: HTMLElement | null = document.getElementById(val);
    if (divId) {
      const txt = divId.textContent;
      if (txt) {
        navigator.clipboard.writeText(txt);
        setIsCopiedTxt(true);
        setTimeout(() => {
          setIsCopiedTxt(false);
        }, 1500);
      }
    }
  };

  useEffect(() => {
    setCodes(() => {
      const url = new URL(
        `${Urls.getEndpointHttpUrl()}/${endpointData.apiKey}/`,
      );
      return [
        {
          lang: 'CURL',
          http: `curl ${url.href} \\
-X POST \\
-H 'Content-Type:application/json' \\
-d '{ "jsonrpc" : "2.0", "id" : 1, "method": "rpc_methods","params":[] }'`,
        },
        {
          lang: 'WSCAT',
          wss: `npx wscat -c wss://${url.hostname}${url.pathname}`,
        },
        {
          lang: 'NodeJs',
          http: `import axios from 'axios';

const data = JSON.stringify({
"jsonrpc": "2.0",
"id": 1,
"method": "rpc_methods",
"params": []
});

const config = {
method: 'post',
url: '${url.href}',
headers: {
'Content-Type': 'application/json'
},
data : data
};

axios(config)
.then(function (response) {
console.log(JSON.stringify(response.data));
})
.catch(function (error) {
console.log(error);
});`,
          wss: `import WebSocket from 'ws';

const ws = new WebSocket('wss://${url.hostname}${url.pathname}');

ws.on('open', function open() {
    console.log('connected');

// NOTE : use delay to avoid missing messages from server
    setTimeout(() => {
            const msg = {"jsonrpc":"2.0","method":"rpc_methods","params":["0x7bf7b17da59880d9bcca24915679668db75f9397", "0x0"],"id":1};
    ws.send(JSON.stringify(msg));
}, 1000);
});

ws.on('message', function message(data) {
console.log('received: %s', data);
});`,
        },
        {
          lang: 'Python',
          http: `import requests

url = '${url.href}'
payload = {
  "jsonrpc": "2.0",
  "id": 1,
  "method": "rpc_methods",
  "params": []
}
                              
x = requests.post(url, json = payload)
print(x.text)`,
          wss: `
import asyncio
from websockets import connect
import time
import json

async def hello(uri):
    async with connect(uri) as websocket:
            print('connected')
            time.sleep(1)
            await websocket.send(json.dumps({"jsonrpc":"2.0","method":"rpc_methods","params":["0x7bf7b17da59880d9bcca24915679668db75f9397", "0x0"],"id":1}))
            message = await websocket.recv()
            print(message)

asyncio.run(hello("wss://${url.hostname}${url.pathname}"))`,
        },
        {
          lang: 'Go',
          http: `
package main

import (
"encoding/json"
"fmt"
"io/ioutil"
"log"
"net/http"
"strings"
)

func main() {
data, err := json.Marshal(map[string]interface{}{
  "jsonrpc": "2.0",
  "method":  "rpc_methods",
  "id":      1,
  "params":  []interface{}{},
})
if err != nil {
  log.Fatalf("Marshal error: %v", err)
}

resp, err := http.Post("${url.href}",
  "application/json",
  strings.NewReader(string(data)),
)
if err != nil {
  log.Fatalf("HTTP request error: %v", err)
}
defer resp.Body.Close()

body, err := ioutil.ReadAll(resp.Body)
if err != nil {
  log.Fatalf("ReadAll error: %v", err)
}

fmt.Println(string(body))
}`,
          wss: `
package main

import (
"log"
"os"
"os/signal"
"time"

"github.com/gorilla/websocket"
)

func main() {
addr := "wss://${url.hostname}${url.pathname}"
interrupt := make(chan os.Signal, 1)
signal.Notify(interrupt, os.Interrupt)

log.Printf("connecting to %s", addr)
c, _, err := websocket.DefaultDialer.Dial(addr, nil)
if err != nil {
log.Fatal("dial error:", err)
}
defer c.Close()

done := make(chan struct{})
go func() {
defer close(done)
for j := 1; j <= 1; j++ {
  _, message, err := c.ReadMessage()
  if err != nil {
    log.Println("read error:", err)
    return
  }
  log.Printf("received: %s", message)
}
}()

ticker := time.NewTicker(time.Second)
defer ticker.Stop()
for {
select {
case <-done:
  log.Println("done")
  return
case <-ticker.C:
  data := \`{ "jsonrpc": "2.0", "id": 1, "method": "rpc_methods", "params": [] }\`
  err = c.WriteMessage(websocket.TextMessage, []byte(data))
  if err != nil {
    log.Println("write error:", err)
    return
  }
  log.Printf("sent: %s", data)
case <-interrupt:
  log.Println("interrupt")
  err := c.WriteMessage(websocket.CloseMessage, websocket.FormatCloseMessage(websocket.CloseNormalClosure, ""))
  if err != nil {
    log.Println("write close:", err)
    return
  }
  select {
  case <-done:
  case <-time.After(time.Second):
  }
  return
}
}
}`,
        },
      ];
    });
  }, [endpointData]);

  return (
    <div className="card py-4 px-4 border shadow-sm h-100">
      <div className="fw-bold text-center mb-4">
        Example for making a request
      </div>
      <p className="text-end mt-2 mb-2">
        <select
          className="form-select"
          defaultValue="Curl"
          onChange={(event) => {
            const selected = event.currentTarget.value as codeSnippetType;
            if (selected === 'WSCAT') setCodeType('wss');
            else setCodeType('https');
            setCodeSnippet(selected);
          }}
        >
          {codes.map((code, index) => (
            <option key={`code-${index.toString()}`} value={code.lang}>
              {code.lang}
            </option>
          ))}
        </select>
      </p>
      <div className="card p-2 endpointcard rounded-2 bg-light">
        <ul className="nav nav-tabs " id="myTab" role="tablist">
          <li
            className="nav-item"
            role="presentation"
            style={{
              display: codeSnippet === 'WSCAT' ? 'none' : 'block',
            }}
            onClick={() => setCodeType('https')}
          >
            <button
              className={`nav-link px-4 ${
                codeType === 'https' ? 'active' : ''
              }`}
              data-bs-toggle="tab"
              type="button"
              role="tab"
            >
              <span className="p-1">HTTP</span>
            </button>
          </li>
          <li
            className="nav-item"
            role="presentation"
            style={{
              display: codeSnippet === 'CURL' ? 'none' : 'block',
            }}
            onClick={() => setCodeType('wss')}
          >
            <button
              className={`nav-link px-4 ${codeType === 'wss' ? 'active' : ''}`}
              data-bs-toggle="tab"
              type="button"
              role="tab"
            >
              <span className="p-1">WS</span>
            </button>
          </li>
        </ul>
        <div className="d-flex justify-content-between gap-2">
          <pre id="example">
            <code className="">
              {codeType === 'https'
                && codes.find((code) => codeSnippet === code.lang)?.http}

              {codeType === 'wss'
                && codes.find((code) => codeSnippet === code.lang)?.wss}
            </code>
          </pre>
          <span>
            <i
              aria-hidden
              className="fa-regular fa-copy fs-6 cursor text-primary p-2"
              onClick={() => {
                let content: string | undefined = '';

                if (codeType === 'https') {
                  content = codes.find(
                    (code) => codeSnippet === code.lang,
                  )?.http;
                } else {
                  content = codes.find(
                    (code) => codeSnippet === code.lang,
                  )?.wss;
                }

                if (content) copyText('example');
              }}
            />
          </span>
        </div>
      </div>
    </div>
  );
}

export default Snippet;
